import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import { Alert, Divider, Grid, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  CalculateByField,
  ConditionEqualityType,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  CellDrillDownInfo,
  DimensionDescriptor,
  DrillDownRow,
  MeasureDrillDownInfo,
} from "../../../../../../../../../api/types/svTypes.ts";
import { CellDrillDownInfoType, MeasureUnitTable } from "../../../../../../../../../api/types/svTypes.ts";
import useUserContext from "../../../../../../../../../context/useUserContext.ts";
import type { FieldWithOrder, FieldWithSorting } from "../../../../../hooks/FieldWithOrder";
import useDrillDown from "../../../../../hooks/useDrillDown";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import {
  selectDimensions,
  selectDimensionsStructureForCurrentReportType,
} from "../../../../../store/selectors/metaDataSelectors";
import { selectDrillDownRootConfiguration } from "../../../../../store/slices/drillDownConfigurationSlice";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";
import { createDrillDownPageAction } from "../../../../../store/utils/storeUtils";
import { createLeafConditionDescriptor } from "../../../../../utilities/conditionDescriptorHelper.ts";
import CollapseContainer from "../../../../common/CollapseContainer.tsx";
import ColumnsOptions from "../columns/ColumnsOptions.tsx";
import ConditionsCollapsedHeader from "../ConditionsCollapsedHeader.tsx";
import ConditionsPanel from "../ConditionsPanel.tsx";
import DrillDownGridWrapper from "../DrillDownGridWrapper.tsx";
import RecordsCounter from "../RecordsCounter.tsx";
import ToolBar from "../ToolBar.tsx";
import type { PersistedDrillDownSorting } from "./drillDownLedgerColumnsStorage";
import { drillDownLedgerColumnsStorage } from "./drillDownLedgerColumnsStorage";
import { getExtensionLedger } from "./extensionLedgers";
import type { LedgerColumnsSetup } from "./ledgerColumnsSetupHelpers";
import { buildDefaultSetup, getLedgerBucket, isCustomSetup, restorePersistedSetup } from "./ledgerColumnsSetupHelpers";
import { getLedgerDataModelName, isDimensionApplicableToLedger } from "./ledgerDimensionFilter";

const emptyDimensions: string[] = [];

interface Props {
  configuration: DrillDownPage;
  info: MeasureDrillDownInfo;
}

export default function LedgerPart({ configuration, info }: Props) {
  const dimensions = useSelector(selectDimensions);
  const dimensionsStructure = useSelector(selectDimensionsStructureForCurrentReportType);
  const rootConfiguration = useSelector(selectDrillDownRootConfiguration);
  const defaultDimensions = useMemo(() => {
    if (!rootConfiguration) {
      return emptyDimensions;
    }

    const extensionConfiguration = getExtensionLedger(info.table)?.getConfiguration(rootConfiguration);
    return extensionConfiguration?.defaultDimensions ?? rootConfiguration.defaultDimensions ?? emptyDimensions;
  }, [rootConfiguration, info.table]);
  const { id: userId } = useUserContext();
  const [columnsInitialized, setColumnsInitialized] = useState<boolean>(false);

  const state = useDrillDown(info);
  const { changeFieldSorting, changeSelectedFields } = state.actions;
  const dispatch = useAppDispatch();

  const [showColumnsOptions, setShowColumnsOptions] = useState<boolean | undefined>();
  const [isCustomColumnsSetupApplied, setIsCustomColumnsSetupApplied] = useState(false);

  const filteredDimensions = useMemo(() => {
    const ledger = getLedgerDataModelName(info);
    return dimensions.filter((d) => isDimensionApplicableToLedger(d, ledger));
  }, [dimensions, info]);

  const withAllocation = useMemo(() => info.allocated === true, [info.allocated]);
  const ledgerBucket = useMemo(() => getLedgerBucket(info), [info]);

  const availableDimensions = useMemo(() => {
    const allowedNames = new Set(defaultDimensions);
    return filteredDimensions.filter((d) => allowedNames.has(d.name));
  }, [filteredDimensions, defaultDimensions]);

  const defaultSetup = useMemo(
    () => buildDefaultSetup(defaultDimensions, withAllocation, availableDimensions),
    [defaultDimensions, withAllocation, availableDimensions]
  );

  const columnsScope = useMemo(
    () => ({
      reportId: configuration.reportId,
      userId,
      ledgerBucket,
    }),
    [configuration.reportId, userId, ledgerBucket]
  );

  const selectedFieldsRef = useRef<FieldWithOrder[]>(state.selectedFields);
  const sortedFieldsRef = useRef<FieldWithSorting[]>(state.sortedFields);
  useEffect(() => {
    selectedFieldsRef.current = state.selectedFields;
  }, [state.selectedFields]);
  useEffect(() => {
    sortedFieldsRef.current = state.sortedFields;
  }, [state.sortedFields]);

  useEffect(() => {
    const persistedSetup = drillDownLedgerColumnsStorage.get(columnsScope);
    const restoredSetup = restorePersistedSetup(persistedSetup, availableDimensions, defaultSetup);

    changeSelectedFields(restoredSetup.selectedFields);
    changeFieldSorting(restoredSetup.sortedFields);
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setColumnsInitialized(true);
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsCustomColumnsSetupApplied(isCustomSetup(restoredSetup, defaultSetup));
  }, [changeFieldSorting, changeSelectedFields, columnsScope, defaultSetup, availableDimensions]);

  const persistSetup = useCallback(
    (setup: LedgerColumnsSetup) => {
      const customSetupApplied = isCustomSetup(setup, defaultSetup);
      setIsCustomColumnsSetupApplied(customSetupApplied);

      if (!customSetupApplied) {
        drillDownLedgerColumnsStorage.clear(columnsScope);
        return;
      }

      drillDownLedgerColumnsStorage.save(columnsScope, {
        selectedColumnNames: setup.selectedFields.map((field) => field.field.name),
        sorting: buildPersistedSorting(setup.sortedFields),
      });
    },
    [columnsScope, defaultSetup]
  );

  const onSelectedFieldsChanged = useCallback(
    (selection: FieldWithOrder[]) => {
      const normalizedSelection = normalizeOrderedFields(selection);
      const normalizedSorting = retainSortingForSelectedFields(sortedFieldsRef.current, normalizedSelection);

      changeSelectedFields(normalizedSelection);
      changeFieldSorting(normalizedSorting);
      persistSetup({ selectedFields: normalizedSelection, sortedFields: normalizedSorting });
    },
    [changeFieldSorting, changeSelectedFields, persistSetup]
  );

  const onSortedFieldsChanged = useCallback(
    (sorting: FieldWithSorting[]) => {
      const normalizedSorting = normalizeOrderedFields(sorting);
      const normalizedSelection = normalizeOrderedFields(selectedFieldsRef.current);

      changeFieldSorting(normalizedSorting);
      persistSetup({ selectedFields: normalizedSelection, sortedFields: normalizedSorting });
    },
    [changeFieldSorting, persistSetup]
  );

  const onColumnsSetupApplied = useCallback(
    (selection: FieldWithOrder[], sorting: FieldWithSorting[]) => {
      const normalizedSelection = normalizeOrderedFields(selection);
      const normalizedSorting = normalizeOrderedFields(sorting);

      changeSelectedFields(normalizedSelection);
      changeFieldSorting(normalizedSorting);
      persistSetup({ selectedFields: normalizedSelection, sortedFields: normalizedSorting });
    },
    [changeFieldSorting, changeSelectedFields, persistSetup]
  );

  const handleSubDrillDown = useCallback(
    (row: DrillDownRow) => {
      const newConfiguration = createSubDrillDown(row, configuration, info, dimensions);
      if (newConfiguration !== undefined) dispatch(createDrillDownPageAction("add", newConfiguration));
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [configuration, info, dimensions]
  );

  const hideColumnsOptions = useCallback(() => setShowColumnsOptions(false), []);

  return (
    <Grid
      container
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <CollapseContainer
        collapseElement={<ConditionsCollapsedHeader conditionsCount={state.conditions.length} />}
        containerSx={{
          height: "100%",
          ".MuiCollapse-root": {
            height: "100%",
          },
        }}
      >
        <ConditionsPanel state={state} configuration={configuration} info={info} />
      </CollapseContainer>
      <Stack sx={{ flex: 1, width: "unset" }}>
        <Stack spacing={2} useFlexGap sx={{ flex: 1, width: "unset", pl: "1.3rem", py: 2, pr: 3 }}>
          <ToolBar
            state={state}
            onShowColumnsOptions={() => setShowColumnsOptions(true)}
            showColumnsCustomBadge={isCustomColumnsSetupApplied}
          />
          {state.error && (
            <Alert sx={{ mb: ".5rem" }} severity="error">
              {state.error}
            </Alert>
          )}
          {!columnsInitialized && <InlineLoader text="Preparing data..." />}
          {columnsInitialized && configuration && info.conditions && info.conditions.length > 0 && (
            <DrillDownGridWrapper
              state={state}
              highlightedColumns={getActiveLayer(configuration).info?.combined.valueTargetFields}
              onDrillDown={handleSubDrillDown}
              info={info}
              onSelectedFieldsChanged={onSelectedFieldsChanged}
              onSortedFieldsChanged={onSortedFieldsChanged}
              disableCellClick={!!configuration.layer?.measure?.config?.persistedAggregationName}
            />
          )}
        </Stack>
        <Divider />
        <Grid container sx={{ backgroundColor: "background.grey", width: "100%", pr: 3 }}>
          <RecordsCounter totalCount={state.rowsTotal} align="right" />
        </Grid>
      </Stack>
      {showColumnsOptions !== undefined && (
        <ColumnsOptions
          dimensions={availableDimensions}
          selectedFields={state.selectedFields}
          sortedFields={state.sortedFields}
          defaultSelectedFields={defaultSetup.selectedFields}
          defaultSortedFields={defaultSetup.sortedFields}
          showCustomSetupAppliedInfo={isCustomColumnsSetupApplied}
          groupStructure={dimensionsStructure}
          open={showColumnsOptions === true}
          withAllocation={withAllocation}
          onApplySelection={onSelectedFieldsChanged}
          onApplySorting={onSortedFieldsChanged}
          onApplySetup={onColumnsSetupApplied}
          onClose={hideColumnsOptions}
        />
      )}
    </Grid>
  );
}

const normalizeOrderedFields = <T extends { order: number }>(fields: T[]): T[] => {
  return [...fields]
    .sort((a, b) => a.order - b.order)
    .map((field, index) => ({
      ...field,
      order: index,
    }));
};

const retainSortingForSelectedFields = (
  sorting: FieldWithSorting[],
  selectedFields: FieldWithOrder[]
): FieldWithSorting[] => {
  const selectedFieldsByName = selectedFields.reduce<Record<string, FieldWithOrder>>((result, field) => {
    result[field.field.name] = field;
    return result;
  }, {});

  return normalizeOrderedFields(sorting)
    .map<FieldWithSorting | undefined>((field, index) => {
      const selectedField = selectedFieldsByName[field.field.name];
      if (selectedField === undefined) {
        return undefined;
      }

      return {
        field: selectedField.field,
        sortAsc: field.sortAsc,
        order: index,
        canBeRemoved: selectedField.canBeRemoved,
      };
    })
    .filter((field): field is FieldWithSorting => field !== undefined);
};

const buildPersistedSorting = (sorting: FieldWithSorting[]): PersistedDrillDownSorting[] => {
  return normalizeOrderedFields(sorting).map((field, index) => ({
    dimensionName: field.field.name,
    sortAsc: field.sortAsc,
    order: index,
  }));
};

function createSubDrillDown(
  row: DrillDownRow,
  configuration: DrillDownPage,
  info: MeasureDrillDownInfo,
  dimensions: DimensionDescriptor[]
) {
  const ledgerEntryNoDimension = dimensions.find((d) => d.name === "LedgerEntryNo");
  const entryNo = row.cells["EntryNo"]?.value;

  if (entryNo == null || ledgerEntryNoDimension === undefined) {
    return undefined;
  }

  const entryNoValue = String(entryNo);
  const value = getSubDrillDownFormattedValue(info, row);
  const measure = getSubDrillDownMeasure(configuration, row);

  const newConfiguration: DrillDownPage = {
    pageId: generateGuid(),
    reportId: configuration.reportId,
    onlyLedger: true,
    chartOfAccounts: false,
    layer: {
      measure,
      cell: {
        value: undefined,
        formattedValue: value || "",
        drillDownId: undefined,
      },
      info: {
        combined: {
          drillDownInfoType: CellDrillDownInfoType.Default,
          field: info.field || CalculateByField.Lcy,
          table: info.table || MeasureUnitTable.Gl,
          allocated: true,
          conditions: [
            createLeafConditionDescriptor({
              dimensionName: ledgerEntryNoDimension.name,
              equalityType: ConditionEqualityType.Equal,
              values: [entryNoValue],
            }),
          ],
          amountType: info.amountType,
        } as CellDrillDownInfo,
        items: [
          {
            drillDownInfoType: CellDrillDownInfoType.Default,
            field: info.field || CalculateByField.Lcy,
            table: info.table || MeasureUnitTable.Gl,
            allocated: true,
            conditions: [
              createLeafConditionDescriptor({
                dimensionName: ledgerEntryNoDimension.name,
                equalityType: ConditionEqualityType.Equal,
                values: [entryNoValue],
              }),
            ],
            amountType: info.amountType,
          } as CellDrillDownInfo,
        ],
      },
    },
  };

  return newConfiguration;
}

function getSubDrillDownFormattedValue(info: MeasureDrillDownInfo, row: DrillDownRow) {
  const valueFieldMapping = {
    [CalculateByField.Lcy]: "AmountLcy",
    [CalculateByField.Pcy]: "AmountPcy",
    [CalculateByField.Shares]: "Shares",
    [CalculateByField.AdditionalLcy]: "AdditionalAmountLcy",
    [CalculateByField.Scy]: "AmountScy",
  };
  const valueField = valueFieldMapping[info?.field || CalculateByField.Lcy];
  return row.cells[valueField]?.formattedValue;
}
function getSubDrillDownMeasure(configuration: DrillDownPage, row: DrillDownRow) {
  const measure = structuredClone(getActiveLayer(configuration).measure);
  if (measure !== undefined) {
    measure.config.customLabel = getSubDrillDownMeasureCaption(row);
  }

  return measure;
}
function getSubDrillDownMeasureCaption(row: DrillDownRow) {
  const accountNo = row.cells["AccountNo"]?.formattedValue;
  const accountName = row.cells["AccountName"]?.formattedValue;
  if (accountNo && accountName) {
    return `${accountNo} - ${accountName}`;
  }

  return accountNo || accountName || "";
}
